import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  Platform,
  Image,
  Switch,
  Alert,
  ActivityIndicator,
  Linking,
  useWindowDimensions,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import { useAuth } from '../services/AuthContext';
import { useFocusEffect } from '@react-navigation/native';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import Icon from 'react-native-vector-icons/MaterialIcons';
import StripeConnectSetup from '../components/StripeConnectSetup';
import { getUserById, listEvents, listTournaments, getClubMembers, getClubMemberCount, updateMemberListVisibility, removeMemberFromClub, getRecurringEvents, deleteRecurringEvent, toggleRecurringEvent } from '../services/database';
import { getSocialConnections, getPendingDraftsCount, disconnectSocialAccount, triggerSyncForClub, triggerParseForClub, getRecentSocialPosts } from '../services/socialSyncService';
import ClubLocationManager from '../components/ClubLocationManager';
import SocialConnectionCard from '../components/SocialConnectionCard';
import RecurringEventCard from '../components/RecurringEventCard';
import { generateRecurringEvents } from '../utils/recurringEventGenerator';

const ClubDashboardScreen = ({ navigation, route }) => {
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth < 768;
  const [activeTab, setActiveTab] = useState('events');
  const [clubData, setClubData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventCount, setEventCount] = useState(0);
  const [tournaments, setTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberListPublic, setMemberListPublic] = useState(true);
  const [refreshLocations, setRefreshLocations] = useState(0);
  const [socialConnections, setSocialConnections] = useState([]);
  const [pendingDraftsCount, setPendingDraftsCount] = useState(0);
  const [loadingSocial, setLoadingSocial] = useState(false);
  const [recentSocialPosts, setRecentSocialPosts] = useState([]);
  const [socialPostsPlatform, setSocialPostsPlatform] = useState(undefined);
  const [eventsRefreshing, setEventsRefreshing] = useState(false);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [loadingRecurring, setLoadingRecurring] = useState(false);

  useEffect(() => {
    fetchClubData();
  }, [appUser]);

  useEffect(() => {
    if (clubData) {
      fetchEvents();
      fetchTournaments();
      fetchMembers();
      fetchRecurringEvents();
    }
  }, [clubData]);

  useFocusEffect(
    React.useCallback(() => {
      const initialTab = route.params?.initialTab;
      if (initialTab === 'info' || initialTab === 'events' || initialTab === 'members' || initialTab === 'tournaments' || initialTab === 'locations' || initialTab === 'payments') {
        setActiveTab(initialTab);
      }
      if (clubData?.approvalStatus === 'APPROVED') {
        fetchEvents();
        fetchTournaments();
        fetchRecurringEvents();
        fetchSocialSync();
        generateRecurringEvents(clubData.id)
          .then(({ created }) => {
            if (created > 0) fetchEvents();
          })
          .catch((e) => console.warn('Auto-generate recurring events:', e));
      }
    }, [clubData])
  );

  const fetchSocialSync = async () => {
    if (!clubData?.id) return;
    setLoadingSocial(true);
    try {
      const [conns, count] = await Promise.all([
        getSocialConnections(clubData.id),
        getPendingDraftsCount(clubData.id),
      ]);
      setSocialConnections(conns || []);
      setPendingDraftsCount(count ?? 0);
      // Fetch latest recent posts preview for this club (used in Club Info tab)
      if ((conns || []).length > 0) {
        try {
          const { posts, platform } = await getRecentSocialPosts(clubData.id, 6);
          setRecentSocialPosts(posts || []);
          setSocialPostsPlatform(platform);
        } catch (e) {
          console.warn('fetchSocialSync recent posts', e);
          setRecentSocialPosts([]);
          setSocialPostsPlatform(undefined);
        }
      } else {
        setRecentSocialPosts([]);
      }
      if ((conns || []).length > 0) {
        triggerSyncForClub(clubData.id)
          .then(() => triggerParseForClub(clubData.id))
          .then(() => {
            getPendingDraftsCount(clubData.id).then((c) => setPendingDraftsCount(c ?? 0));
            // Refresh recent posts after parse so new content appears in preview
            getRecentSocialPosts(clubData.id, 6)
              .then(({ posts, platform }) => {
                setRecentSocialPosts(posts || []);
                setSocialPostsPlatform(platform);
              })
              .catch((e) => console.warn('refresh recent posts after parse', e));
          })
          .catch((e) => console.warn('Social sync/parse background:', e));
      }
    } catch (e) {
      console.error('fetchSocialSync', e);
    } finally {
      setLoadingSocial(false);
    }
  };

  const handleManualSocialRefresh = async () => {
    if (!clubData?.id) return;
    const previousCount = pendingDraftsCount ?? 0;
    setLoadingSocial(true);
    try {
      const conns = await getSocialConnections(clubData.id);
      setSocialConnections(conns || []);

      if (!conns || conns.length === 0) {
        showToast?.('Connect Instagram first', 'error');
        return;
      }

      const syncRes = await triggerSyncForClub(clubData.id);
      const parseRes = await triggerParseForClub(clubData.id);

      const newCount = await getPendingDraftsCount(clubData.id);
      setPendingDraftsCount(newCount ?? 0);
      const added = (newCount ?? 0) - previousCount;

      const processed = typeof syncRes?.processed === 'number' ? syncRes.processed : 0;
      const parsed = typeof parseRes?.parsed === 'number' ? parseRes.parsed : 0;
      const errCount = syncRes?.errors && typeof syncRes.errors === 'object' ? Object.keys(syncRes.errors).length : 0;
      if (errCount > 0) {
        const firstKey = Object.keys(syncRes.errors)[0];
        const firstMsg = syncRes.errors[firstKey];
        showToast?.(`Sync errors (${errCount}): ${String(firstMsg).slice(0, 120)}`, 'error');
      } else {
        if (added > 0) {
          showToast?.(
            `Added ${added} new draft${added === 1 ? '' : 's'} • Total ${newCount ?? 0}`,
            'success'
          );
        } else if (processed > 0 || parsed > 0) {
          showToast?.(`Synced ${processed} posts • Created ${parsed} drafts`, 'success');
        } else {
          showToast?.(`No new drafts. You still have ${newCount ?? 0}.`, 'success');
        }
      }
      // After a manual refresh, update recent posts preview as well
      try {
        const { posts, platform } = await getRecentSocialPosts(clubData.id, 6);
        setRecentSocialPosts(posts || []);
        setSocialPostsPlatform(platform);
      } catch (e) {
        console.warn('manual refresh recent posts', e);
      }
    } catch (e) {
      showToast?.(e?.message || 'Social sync failed', 'error');
      console.warn('Manual social refresh failed:', e);
    } finally {
      setLoadingSocial(false);
    }
  };

  const handleDisconnectSocial = async (connectionId) => {
    try {
      await disconnectSocialAccount(connectionId);
      showToast?.('Disconnected', 'success');
      fetchSocialSync();
    } catch (e) {
      showToast?.(e.message || 'Failed to disconnect', 'error');
    }
  };

  const fetchClubData = async () => {
    if (!appUser?.id) return;
    
    try {
      const userData = await getUserById(appUser.id);
      if (userData) {
        // Convert snake_case to camelCase
        setClubData({
          id: userData.id,
          username: userData.username,
          email: userData.email,
          clubName: userData.club_name,
          address: userData.address,
          latitude: userData.latitude,
          longitude: userData.longitude,
          about: userData.about,
          website: userData.website,
          schedule: userData.schedule,
          accountType: userData.account_type,
          tier: userData.tier,
          claimedBy: userData.claimed_by,
          stripeAccountId: userData.stripe_account_id,
          stripeOnboardingComplete: userData.stripe_onboarding_complete,
          approvalStatus: userData.approval_status,
          rejectionReason: userData.rejection_reason,
          submittedAt: userData.submitted_at,
          memberListPublic: userData.member_list_public !== false, // default to true
        });
        setMemberListPublic(userData.member_list_public !== false);
      }
    } catch (error) {
      console.error('Error fetching club data:', error);
    }
  };

  const handleStripeUpdate = () => {
    fetchClubData(); // Refresh club data after Stripe setup
  };

  const getEventEndDate = (event) => {
    const start = new Date(event.event_date);
    if (event.end_time) {
      const endStr = String(event.end_time).trim();
      if (endStr.includes('T') || /^\d{4}-\d{2}/.test(endStr)) {
        return new Date(endStr);
      }
      const [hours, minutes] = endStr.split(':').map(Number);
      const end = new Date(start);
      end.setHours(isNaN(hours) ? 0 : hours, isNaN(minutes) ? 0 : minutes, 0, 0);
      return end;
    }
    const end = new Date(start);
    end.setHours(end.getHours() + 4, 0, 0, 0);
    return end;
  };

  const fetchEvents = async (isPullRefresh = false) => {
    if (!clubData?.id) {
      setLoading(false);
      return;
    }

    if (!isPullRefresh) setLoading(true);
    if (isPullRefresh) setEventsRefreshing(true);
    try {
      const allEvents = await listEvents({ clubId: clubData.id });
      const now = new Date();
      const upcomingOrOngoingEvents = (allEvents || []).filter((event) => {
        const endDate = getEventEndDate(event);
        return endDate >= now;
      });

      // Convert snake_case to camelCase for events
      const formattedEvents = upcomingOrOngoingEvents.map(e => ({
        id: e.id,
        title: e.title,
        eventDate: e.event_date,
        eventTime: e.event_time,
        eventType: e.event_type,
        location: e.location,
        description: e.description,
        capacity: e.capacity,
        rsvps: e.rsvps || [],
      }));

      setEvents(formattedEvents);
      setEventCount(allEvents?.length || 0);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
      setEventsRefreshing(false);
    }
  };

  const fetchTournaments = async () => {
    if (!clubData?.id) {
      setLoadingTournaments(false);
      return;
    }

    try {
      setLoadingTournaments(true);
      const result = await listTournaments({ clubId: clubData.id });

      // Convert snake_case to camelCase for tournaments
      const formattedTournaments = (result || []).map(t => ({
        id: t.id,
        name: t.name,
        status: t.status,
        type: t.type,
        totalRounds: t.total_rounds,
        currentRound: t.current_round,
        allowPhones: t.allow_phones,
        startTime: t.start_time,
      }));

      setTournaments(formattedTournaments);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      showToast('Failed to load tournaments', 'error');
    } finally {
      setLoadingTournaments(false);
    }
  };

  const fetchMembers = async () => {
    if (!clubData?.id) {
      setLoadingMembers(false);
      return;
    }

    try {
      setLoadingMembers(true);
      const [membersData, count] = await Promise.all([
        getClubMembers(clubData.id, false, 100),
        getClubMemberCount(clubData.id),
      ]);
      setMembers(membersData || []);
      setMemberCount(count || 0);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleToggleMemberListVisibility = async (value) => {
    try {
      setMemberListPublic(value);
      await updateMemberListVisibility(clubData.id, value);
      showToast(
        value ? 'Member list is now public' : 'Member list is now private',
        'success'
      );
    } catch (error) {
      console.error('Error updating member list visibility:', error);
      setMemberListPublic(!value); // Revert on error
      showToast('Failed to update visibility', 'error');
    }
  };

  const handleRemoveMember = async (member) => {
    const confirmRemove = Platform.OS === 'web'
      ? window.confirm(`Are you sure you want to remove ${member.user?.username || 'this member'} from the club?`)
      : await new Promise((resolve) => {
          Alert.alert(
            'Remove Member',
            `Are you sure you want to remove ${member.user?.username || 'this member'} from the club?`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Remove', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmRemove) return;

    try {
      await removeMemberFromClub(clubData.id, member.user_id);
      setMembers(prev => prev.filter(m => m.id !== member.id));
      setMemberCount(prev => Math.max(0, prev - 1));
      showToast('Member removed', 'success');
    } catch (error) {
      console.error('Error removing member:', error);
      showToast('Failed to remove member', 'error');
    }
  };

  const formatJoinDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatEventDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[date.getDay()];
    return `${dayName} ${timeStr || ''}`;
  };

  const fetchRecurringEvents = async () => {
    if (!clubData?.id) return;
    setLoadingRecurring(true);
    try {
      const data = await getRecurringEvents(clubData.id);
      setRecurringEvents(data || []);
    } catch (e) {
      console.error('Error fetching recurring events:', e);
    } finally {
      setLoadingRecurring(false);
    }
  };

  const handleDeleteRecurring = async (id) => {
    try {
      await deleteRecurringEvent(id);
      showToast('Schedule deleted', 'success');
      fetchRecurringEvents();
    } catch (e) {
      console.error('Error deleting recurring event:', e);
      showToast('Failed to delete schedule', 'error');
    }
  };

  const handleToggleRecurring = async (id, isActive) => {
    try {
      await toggleRecurringEvent(id, isActive);
      showToast(isActive ? 'Schedule resumed' : 'Schedule paused', 'success');
      fetchRecurringEvents();
    } catch (e) {
      console.error('Error toggling recurring event:', e);
      showToast('Failed to update schedule', 'error');
    }
  };

  const dynamicStyles = useMemo(() => ({
    container: { backgroundColor: colors.background },
    header: { borderBottomColor: colors.borderLight },
    headerTitle: { color: colors.text },
    tabs: { borderBottomColor: colors.borderLight, backgroundColor: colors.background },
    tabText: { color: colors.textSecondary },
    activeTabText: { color: colors.text, fontWeight: '600' },
    tabContent: { backgroundColor: colors.background },
    analyticsButton: { backgroundColor: colors.surface, borderColor: colors.border },
    analyticsButtonText: { color: colors.text },
    infoSection: { borderBottomColor: colors.borderLight },
    infoTitle: { color: colors.text },
    infoText: { color: colors.textSecondary },
    emptyStateText: { color: colors.textSecondary },
    emptyStateSubtext: { color: colors.textLight },
    memberHeaderSection: { backgroundColor: colors.surface },
    memberCountText: { color: colors.text },
    visibilityLabel: { color: colors.textSecondary },
    visibilityHelpText: { color: colors.textLight },
    memberCard: { backgroundColor: colors.surface, borderColor: colors.border },
    memberAvatarPlaceholder: { backgroundColor: colors.borderLight },
    memberName: { color: colors.text },
    memberJoinDate: { color: colors.textLight },
    memberLocationText: { color: colors.textLight },
    removeMemberButton: { backgroundColor: isDarkMode ? 'rgba(244,67,54,0.2)' : '#FFEBEE' },
    eventCard: { backgroundColor: colors.surface, borderColor: colors.border },
    tournamentCard: { backgroundColor: colors.surface, borderColor: colors.border },
    eventTitle: { color: colors.text },
    eventDateTime: { color: colors.textSecondary },
    eventTypeText: { color: colors.text },
    rsvpCount: { color: colors.textSecondary },
    createButton: { backgroundColor: colors.surface, borderColor: colors.border },
    createButtonText: { color: colors.text },
    createButtonTextDisabled: { color: colors.textLight },
    duplicateButton: { backgroundColor: isDarkMode ? 'rgba(148,235,152,0.2)' : '#E8F5E9', borderColor: '#94EB98' },
    duplicateButtonText: { color: colors.text },
    recurringSection: { backgroundColor: colors.surface, borderColor: colors.border },
    recurringSectionTitle: { color: colors.text },
    addRecurringText: { color: colors.text },
    recurringEmptyText: { color: colors.textLight },
    unclaimedBadge: { backgroundColor: isDarkMode ? 'rgba(255,152,0,0.25)' : '#FFE0B2' },
    tierBadge: { backgroundColor: colors.borderLight },
    tierBadgePro: { backgroundColor: isDarkMode ? 'rgba(148,235,152,0.2)' : '#E8F5E9' },
    tierBadgeText: { color: colors.textSecondary },
    warningBanner: { backgroundColor: isDarkMode ? 'rgba(255,179,0,0.15)' : '#FFF3E0' },
    warningText: { color: colors.text },
    upgradeBanner: { backgroundColor: isDarkMode ? 'rgba(148,235,152,0.15)' : '#E8F5E9' },
    upgradeBannerTitle: { color: colors.text },
    upgradeBannerText: { color: colors.textSecondary },
    upgradeBannerButtonText: { color: colors.text },
    approvalBlockContainer: { backgroundColor: colors.background },
    approvalBlockCard: { backgroundColor: colors.surface },
    approvalBlockTitle: { color: colors.text },
    approvalBlockText: { color: colors.textSecondary },
    rejectionReasonContainer: { backgroundColor: isDarkMode ? 'rgba(244,67,54,0.2)' : '#FFEBEE' },
    rejectionReasonLabel: { color: colors.error },
    rejectionReasonText: { color: colors.error },
    submittedAtText: { color: colors.textLight },
    goBackButtonText: { color: colors.text },
  }), [colors, isDarkMode]);

  const renderClubInfo = () => (
    <ScrollView style={[styles.tabContent, dynamicStyles.tabContent]} showsVerticalScrollIndicator={false}>
      <View style={[styles.infoSection, dynamicStyles.infoSection]}>
        <View style={styles.infoHeader}>
          <Icon name="location-on" size={24} color={colors.textSecondary} />
          <Text style={[styles.infoTitle, dynamicStyles.infoTitle]}>Location</Text>
        </View>
        <Text style={[styles.infoText, dynamicStyles.infoText]}>{clubData?.address || 'No address provided'}</Text>
      </View>

      <View style={[styles.infoSection, dynamicStyles.infoSection]}>
        <View style={styles.infoHeader}>
          <Icon name="schedule" size={24} color={colors.textSecondary} />
          <Text style={[styles.infoTitle, dynamicStyles.infoTitle]}>Regular Schedule</Text>
        </View>
        <Text style={[styles.infoText, dynamicStyles.infoText]}>{clubData?.schedule || 'No schedule provided'}</Text>
      </View>

      <View style={[styles.infoSection, dynamicStyles.infoSection]}>
        <View style={styles.infoHeader}>
          <Icon name="info" size={24} color={colors.textSecondary} />
          <Text style={[styles.infoTitle, dynamicStyles.infoTitle]}>About</Text>
        </View>
        <Text style={[styles.infoText, dynamicStyles.infoText]}>{clubData?.about || 'No description provided'}</Text>
      </View>

      {clubData?.website && (
        <View style={[styles.infoSection, dynamicStyles.infoSection]}>
          <View style={styles.infoHeader}>
            <Icon name="language" size={24} color={colors.textSecondary} />
            <Text style={[styles.infoTitle, dynamicStyles.infoTitle]}>Website</Text>
          </View>
          <Text style={[styles.infoText, styles.link]}>{clubData.website}</Text>
        </View>
      )}

      {/* Recent Instagram/Facebook posts preview (for connected social accounts) */}
      {recentSocialPosts.length > 0 && (
        <View style={[styles.infoSection, dynamicStyles.infoSection]}>
          <View style={styles.infoHeader}>
            <Icon name="photo-library" size={24} color={colors.textSecondary} />
            <Text style={[styles.infoTitle, dynamicStyles.infoTitle]}>
              {socialPostsPlatform === 'instagram'
                ? 'Recent Posts from Instagram'
                : socialPostsPlatform === 'facebook'
                  ? 'Recent Posts from Facebook'
                  : 'Recent Posts'}
            </Text>
          </View>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.recentPostsRow}
          >
            {recentSocialPosts.map((post) => {
              let payload = post.raw_payload || {};
              if (typeof payload === 'string') {
                try {
                  payload = JSON.parse(payload);
                } catch {
                  payload = {};
                }
              }
              const imageUrl =
                (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                post.media_url;

              return (
                <TouchableOpacity
                  key={post.id}
                  style={styles.recentPostThumb}
                  activeOpacity={0.8}
                  onPress={() => {
                    if (!post.permalink) return;
                    if (Platform.OS === 'web') {
                      window.open(post.permalink, '_blank');
                    } else {
                      try {
                        Linking.openURL(post.permalink);
                      } catch {}
                    }
                  }}
                >
                  {imageUrl ? (
                    <Image source={{ uri: imageUrl }} style={styles.recentPostImage} />
                  ) : (
                    <View style={styles.recentPostPlaceholder}>
                      <Icon name="image" size={24} color={colors.textLight} />
                    </View>
                  )}
                  {post.caption ? (
                    <Text style={[styles.recentPostCaption, { color: colors.textSecondary }]} numberOfLines={2}>{post.caption}</Text>
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      )}

      {clubData?.approvalStatus === 'APPROVED' && clubData?.id && (
        <SocialConnectionCard
          clubId={clubData.id}
          connections={socialConnections}
          pendingDraftsCount={pendingDraftsCount}
          loading={loadingSocial}
          onDisconnect={handleDisconnectSocial}
          onReviewDrafts={() => navigation.navigate('SocialDraftReview', { clubId: clubData.id })}
          onRefresh={handleManualSocialRefresh}
        />
      )}
    </ScrollView>
  );

  const renderEvents = () => {
    const isPro = clubData?.tier === 'PRO';
    const isClaimed = !!clubData?.claimedBy;
    const isFreeTier = !isPro;
    const FREE_TIER_LIMIT = 3;
    const canCreateMoreEvents = isPro || eventCount < FREE_TIER_LIMIT;

    return (
      <View style={[styles.tabContent, dynamicStyles.tabContent]}>
        {/* Attendance Analytics */}
        {isClaimed && (
          <TouchableOpacity
            style={[styles.analyticsButton, dynamicStyles.analyticsButton]}
            onPress={() => navigation.navigate('AttendanceAnalytics', { clubId: clubData?.id })}
            activeOpacity={0.8}
          >
            <Icon name="bar-chart" size={20} color={colors.text} />
            <Text style={[styles.analyticsButtonText, dynamicStyles.analyticsButtonText]}>Attendance Analytics</Text>
            <Icon name="chevron-right" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        )}

        {/* Tier Upgrade Banner */}
        {!isClaimed && (
          <View style={[styles.warningBanner, dynamicStyles.warningBanner]}>
            <Icon name="warning" size={20} color="#FF9800" />
            <Text style={[styles.warningText, dynamicStyles.warningText]}>
              Claim your club to create events
            </Text>
            <TouchableOpacity
              style={styles.warningButton}
              onPress={() => navigation.navigate('UserDetail', { userId: clubData?.id })}
            >
              <Text style={styles.warningButtonText}>Claim Now</Text>
            </TouchableOpacity>
          </View>
        )}

        {isClaimed && isFreeTier && eventCount >= FREE_TIER_LIMIT && (
          <View style={[styles.upgradeBanner, dynamicStyles.upgradeBanner]}>
            <Icon name="star" size={20} color="#94EB98" />
            <View style={styles.upgradeBannerContent}>
              <Text style={[styles.upgradeBannerTitle, dynamicStyles.upgradeBannerTitle]}>Upgrade to Pro</Text>
              <Text style={[styles.upgradeBannerText, dynamicStyles.upgradeBannerText]}>
                You've reached the free tier limit ({eventCount}/{FREE_TIER_LIMIT} events)
              </Text>
            </View>
            <TouchableOpacity
              style={styles.upgradeBannerButton}
              onPress={() => navigation.navigate('UpgradeClub', { clubId: clubData?.id })}
            >
              <Text style={[styles.upgradeBannerButtonText, dynamicStyles.upgradeBannerButtonText]}>Upgrade</Text>
            </TouchableOpacity>
          </View>
        )}

        <ScrollView
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={eventsRefreshing}
              onRefresh={() => { fetchEvents(true); fetchRecurringEvents(); }}
            />
          }
        >
          {/* Recurring Schedules Section */}
          {isClaimed && (
            <View style={[styles.recurringSection, dynamicStyles.recurringSection]}>
              <View style={styles.recurringSectionHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Icon name="autorenew" size={20} color={colors.text} />
                  <Text style={[styles.recurringSectionTitle, dynamicStyles.recurringSectionTitle]}>Recurring Schedules</Text>
                </View>
                <TouchableOpacity
                  style={styles.addRecurringButton}
                  onPress={() => navigation.navigate('EditRecurringEvent', { clubId: clubData?.id })}
                  activeOpacity={0.7}
                >
                  <Icon name="add" size={18} color={colors.text} />
                  <Text style={[styles.addRecurringText, dynamicStyles.addRecurringText]}>Add</Text>
                </TouchableOpacity>
              </View>
              {loadingRecurring ? (
                <ActivityIndicator size="small" color="#94EB98" style={{ marginVertical: 12 }} />
              ) : recurringEvents.length === 0 ? (
                <Text style={[styles.recurringEmptyText, dynamicStyles.recurringEmptyText]}>
                  No recurring schedules yet. Create one to auto-generate events each week.
                </Text>
              ) : (
                recurringEvents.map((schedule) => (
                  <RecurringEventCard
                    key={schedule.id}
                    schedule={schedule}
                    onEdit={(s) => navigation.navigate('EditRecurringEvent', { recurringId: s.id, clubId: clubData?.id })}
                    onToggle={handleToggleRecurring}
                    onDelete={handleDeleteRecurring}
                  />
                ))
              )}
            </View>
          )}

          {/* Upcoming Events */}
          {loading ? (
            <View style={styles.loadingContainer}>
              <LoadingAnimation size={375} />
            </View>
          ) : events.length === 0 ? (
            <View style={styles.emptyState}>
              <Icon name="event" size={64} color={colors.textLight} />
              <Text style={[styles.emptyStateText, dynamicStyles.emptyStateText]}>No upcoming events</Text>
              <Text style={[styles.emptyStateSubtext, dynamicStyles.emptyStateSubtext]}>Create your first event or set up a recurring schedule</Text>
            </View>
          ) : (
            events.map((event) => (
              <TouchableOpacity
                key={event.id}
                style={[styles.eventCard, dynamicStyles.eventCard]}
                onPress={() => navigation.navigate('ManageEvent', { eventId: event.id })}
                activeOpacity={0.7}
              >
                <View style={styles.eventCardContent}>
                  <Text style={[styles.eventTitle, dynamicStyles.eventTitle]}>{event.title}</Text>
                  <Text style={[styles.eventDateTime, dynamicStyles.eventDateTime]}>
                    {formatEventDateTime(event.eventDate, event.eventTime)}
                  </Text>
                  <View style={styles.eventMeta}>
                    <View style={styles.eventTypeTag}>
                      <Text style={[styles.eventTypeText, dynamicStyles.eventTypeText]}>{event.eventType}</Text>
                    </View>
                    <Text style={[styles.rsvpCount, dynamicStyles.rsvpCount]}>
                      {event.rsvps?.items?.length || 0} RSVPs
                    </Text>
                  </View>
                </View>
                <Icon name="chevron-right" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            ))
          )}
        </ScrollView>

        <View style={styles.createButtonRow}>
          <TouchableOpacity
            style={[styles.createButton, styles.createButtonFlex, (!canCreateMoreEvents || !isClaimed) && styles.createButtonDisabled, dynamicStyles.createButton]}
            onPress={() => {
              if (!isClaimed) {
                navigation.navigate('UserDetail', { userId: clubData?.id });
              } else if (!canCreateMoreEvents) {
                navigation.navigate('UpgradeClub', { clubId: clubData?.id });
              } else {
                navigation.navigate('CreateEvent');
              }
            }}
            activeOpacity={0.8}
            disabled={!canCreateMoreEvents || !isClaimed}
          >
            <Icon name="add" size={24} color={(!canCreateMoreEvents || !isClaimed) ? colors.textLight : colors.text} />
            <Text style={[styles.createButtonText, dynamicStyles.createButtonText, (!canCreateMoreEvents || !isClaimed) && dynamicStyles.createButtonTextDisabled]}>
              {!isClaimed ? 'Claim Club to Create Events' : !canCreateMoreEvents ? 'Upgrade to Create More' : 'Create New Event'}
            </Text>
          </TouchableOpacity>
          {isClaimed && events.length > 0 && (
            <TouchableOpacity
              style={[styles.duplicateButton, dynamicStyles.duplicateButton]}
              onPress={() => {
                const lastEvent = events[events.length - 1];
                navigation.navigate('CreateEvent', {
                  prefillEvent: {
                    title: lastEvent.title,
                    eventType: lastEvent.eventType,
                    location: lastEvent.location,
                    description: lastEvent.description,
                    capacity: lastEvent.capacity,
                  },
                });
              }}
              activeOpacity={0.8}
            >
              <Icon name="content-copy" size={20} color={colors.text} />
              <Text style={[styles.duplicateButtonText, dynamicStyles.duplicateButtonText]}>Duplicate Last</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderTournaments = () => {
    const isClaimed = !!clubData?.claimedBy;
    const isPro = clubData?.tier === 'PRO';

    return (
      <View style={[styles.tabContent, dynamicStyles.tabContent]}>
        {!isClaimed && (
          <View style={[styles.warningBanner, dynamicStyles.warningBanner]}>
            <Icon name="warning" size={20} color="#FF9800" />
            <Text style={[styles.warningText, dynamicStyles.warningText]}>
              Claim your club to unlock tournament controls
            </Text>
            <TouchableOpacity
              style={styles.warningButton}
              onPress={() => navigation.navigate('UserDetail', { userId: clubData?.id })}
            >
              <Text style={styles.warningButtonText}>Claim Now</Text>
            </TouchableOpacity>
          </View>
        )}

        {isClaimed && !isPro && (
          <View style={[styles.upgradeBanner, dynamicStyles.upgradeBanner]}>
            <Icon name="emoji-events" size={20} color="#FFB300" />
            <View style={styles.upgradeBannerContent}>
              <Text style={[styles.upgradeBannerTitle, dynamicStyles.upgradeBannerTitle]}>Tournament Suite (Pro)</Text>
              <Text style={[styles.upgradeBannerText, dynamicStyles.upgradeBannerText]}>
                Pro tier unlocks Swiss/round-robin pairings, projector view, and phone check-ins.
              </Text>
            </View>
            <TouchableOpacity
              style={styles.upgradeBannerButton}
              onPress={() => navigation.navigate('UpgradeClub', { clubId: clubData?.id })}
            >
              <Text style={[styles.upgradeBannerButtonText, dynamicStyles.upgradeBannerButtonText]}>Upgrade</Text>
            </TouchableOpacity>
          </View>
        )}

        <ScrollView showsVerticalScrollIndicator={false}>
          {loadingTournaments ? (
            <View style={styles.loadingContainer}>
              <LoadingAnimation size={375} />
            </View>
          ) : tournaments.length === 0 ? (
            <View style={styles.emptyState}>
              <Icon name="emoji-events" size={64} color={colors.textLight} />
              <Text style={[styles.emptyStateText, dynamicStyles.emptyStateText]}>No tournaments yet</Text>
              <Text style={[styles.emptyStateSubtext, dynamicStyles.emptyStateSubtext]}>
                Create a Swiss or round-robin event to get started.
              </Text>
            </View>
          ) : (
            tournaments.map((tournamentItem) => (
              <TouchableOpacity
                key={tournamentItem.id}
                style={[styles.tournamentCard, dynamicStyles.tournamentCard]}
                onPress={() =>
                  navigation.navigate('TournamentDetail', { tournamentId: tournamentItem.id })
                }
              >
                <View style={styles.eventCardContent}>
                  <Text style={[styles.eventTitle, dynamicStyles.eventTitle]}>{tournamentItem.name}</Text>
                  <Text style={[styles.eventDateTime, dynamicStyles.eventDateTime]}>
                    {tournamentItem.type?.replace('_', ' ')} • Round {tournamentItem.currentRound || 0}
                  </Text>
                  <View style={styles.eventMeta}>
                    <View style={styles.eventTypeTag}>
                      <Text style={[styles.eventTypeText, dynamicStyles.eventTypeText]}>{tournamentItem.status}</Text>
                    </View>
                  </View>
                </View>
                <Icon name="chevron-right" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            ))
          )}
        </ScrollView>

        <TouchableOpacity
          style={[
            styles.createButton,
            dynamicStyles.createButton,
            (!isPro || !isClaimed) && styles.createButtonDisabled,
          ]}
          onPress={() => {
            if (!isClaimed) {
              navigation.navigate('UserDetail', { userId: clubData?.id });
              return;
            }
            if (!isPro) {
              navigation.navigate('UpgradeClub', { clubId: clubData?.id });
              return;
            }
            navigation.navigate('TournamentWizard', { clubId: clubData?.id });
          }}
          disabled={!isPro || !isClaimed}
          activeOpacity={0.8}
        >
          <Icon
            name="emoji-events"
            size={24}
            color={!isPro || !isClaimed ? colors.textLight : colors.text}
          />
          <Text
            style={[
              styles.createButtonText,
              dynamicStyles.createButtonText,
              (!isPro || !isClaimed) && dynamicStyles.createButtonTextDisabled,
            ]}
          >
            {!isClaimed
              ? 'Claim Club to Run Tournaments'
              : !isPro
              ? 'Upgrade to Run Tournaments'
              : 'New Tournament'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderMembers = () => (
    <View style={[styles.tabContent, dynamicStyles.tabContent]}>
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Member Count & Visibility Toggle */}
        <View style={[styles.memberHeaderSection, dynamicStyles.memberHeaderSection]}>
          <View style={styles.memberCountContainer}>
            <Icon name="people" size={28} color={colors.primary} />
            <Text style={[styles.memberCountText, dynamicStyles.memberCountText]}>
              {memberCount} {memberCount === 1 ? 'Member' : 'Members'}
            </Text>
          </View>
          <View style={styles.visibilityToggleContainer}>
            <Text style={[styles.visibilityLabel, dynamicStyles.visibilityLabel]}>
              {memberListPublic ? 'Public' : 'Private'}
            </Text>
            <Switch
              value={memberListPublic}
              onValueChange={handleToggleMemberListVisibility}
              trackColor={{ false: '#ccc', true: '#81C784' }}
              thumbColor={memberListPublic ? '#4CAF50' : '#f4f3f4'}
            />
          </View>
        </View>

        <Text style={[styles.visibilityHelpText, dynamicStyles.visibilityHelpText]}>
          {memberListPublic 
            ? 'Anyone can see your member list' 
            : 'Only members can see other members'}
        </Text>

        {/* Members List */}
        {loadingMembers ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : members.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="person-add" size={64} color={colors.textLight} />
            <Text style={[styles.emptyStateText, dynamicStyles.emptyStateText]}>No members yet</Text>
            <Text style={[styles.emptyStateSubtext, dynamicStyles.emptyStateSubtext]}>
              When players join your club, they'll appear here
            </Text>
          </View>
        ) : (
          <View style={styles.membersList}>
            {members.map((member) => (
              <View key={member.id} style={[styles.memberCard, dynamicStyles.memberCard]}>
                <View style={styles.memberInfo}>
                  {member.user?.profile_picture_url ? (
                    <Image
                      source={{ uri: member.user.profile_picture_url }}
                      style={styles.memberAvatar}
                    />
                  ) : (
                    <View style={[styles.memberAvatarPlaceholder, dynamicStyles.memberAvatarPlaceholder]}>
                      <Icon name="person" size={24} color={colors.textLight} />
                    </View>
                  )}
                  <View style={styles.memberDetails}>
                    <Text style={[styles.memberName, dynamicStyles.memberName]}>
                      {member.user?.username || 'Unknown User'}
                    </Text>
                    <Text style={[styles.memberJoinDate, dynamicStyles.memberJoinDate]}>
                      Joined {formatJoinDate(member.joined_at)}
                    </Text>
                    {member.user?.city && (
                      <View style={styles.memberLocation}>
                        <Icon name="location-on" size={12} color={colors.textLight} />
                        <Text style={[styles.memberLocationText, dynamicStyles.memberLocationText]}>{member.user.city}</Text>
                      </View>
                    )}
                  </View>
                </View>
                <TouchableOpacity
                  style={[styles.removeMemberButton, dynamicStyles.removeMemberButton]}
                  onPress={() => handleRemoveMember(member)}
                >
                  <Icon name="close" size={20} color="#F44336" />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );

  const renderLocations = () => {
    if (!clubData) {
      return (
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={375} />
        </View>
      );
    }

    return (
      <View style={[styles.tabContent, dynamicStyles.tabContent]}>
        <ClubLocationManager
          clubId={clubData.id}
          currentLocation={{
            latitude: clubData.latitude,
            longitude: clubData.longitude,
            address: clubData.address,
          }}
          onLocationUpdated={() => {
            fetchClubData(); // Refresh club data to get updated location
            setRefreshLocations(prev => prev + 1);
          }}
        />
      </View>
    );
  };

  const renderPayments = () => (
    <ScrollView style={[styles.tabContent, dynamicStyles.tabContent]} showsVerticalScrollIndicator={false}>
      {clubData ? (
        <StripeConnectSetup
          clubId={clubData.id}
          clubName={clubData.clubName || clubData.username}
          email={clubData.email}
          stripeAccountId={clubData.stripeAccountId}
          stripeOnboardingComplete={clubData.stripeOnboardingComplete}
          onUpdate={handleStripeUpdate}
        />
      ) : (
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={375} />
        </View>
      )}
    </ScrollView>
  );

  // Show approval status screen for pending/rejected clubs
  if (clubData && clubData.approvalStatus && clubData.approvalStatus !== 'APPROVED') {
    return (
      <View style={[styles.container, dynamicStyles.container]}>
        <View style={[styles.header, dynamicStyles.header]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Icon name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, dynamicStyles.headerTitle]}>{clubData.clubName || 'Club Dashboard'}</Text>
          <View style={{ width: 40 }} />
        </View>
        <View style={[styles.approvalBlockContainer, dynamicStyles.approvalBlockContainer]}>
          <View style={[
            styles.approvalBlockCard,
            dynamicStyles.approvalBlockCard,
            clubData.approvalStatus === 'PENDING' && styles.approvalBlockPending,
            clubData.approvalStatus === 'REJECTED' && styles.approvalBlockRejected,
          ]}>
            <Icon 
              name={clubData.approvalStatus === 'PENDING' ? 'hourglass-empty' : 'error-outline'} 
              size={64} 
              color={clubData.approvalStatus === 'PENDING' ? '#FF9800' : '#F44336'} 
            />
            <Text style={[styles.approvalBlockTitle, dynamicStyles.approvalBlockTitle]}>
              {clubData.approvalStatus === 'PENDING' 
                ? 'Pending Approval' 
                : 'Application Rejected'}
            </Text>
            <Text style={[styles.approvalBlockText, dynamicStyles.approvalBlockText]}>
              {clubData.approvalStatus === 'PENDING'
                ? 'Your club application is currently being reviewed. You will receive an email once your club is approved.'
                : 'Unfortunately, your club application was not approved at this time.'}
            </Text>
            {clubData.approvalStatus === 'REJECTED' && clubData.rejectionReason && (
              <View style={[styles.rejectionReasonContainer, dynamicStyles.rejectionReasonContainer]}>
                <Text style={[styles.rejectionReasonLabel, dynamicStyles.rejectionReasonLabel]}>Reason:</Text>
                <Text style={[styles.rejectionReasonText, dynamicStyles.rejectionReasonText]}>{clubData.rejectionReason}</Text>
              </View>
            )}
            {clubData.submittedAt && (
              <Text style={[styles.submittedAtText, dynamicStyles.submittedAtText]}>
                Submitted: {new Date(clubData.submittedAt).toLocaleDateString()}
              </Text>
            )}
            <TouchableOpacity 
              style={styles.goBackButton}
              onPress={() => navigation.navigate('Profile')}
            >
              <Text style={[styles.goBackButtonText, dynamicStyles.goBackButtonText]}>Go to Profile</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, dynamicStyles.container]}>
      {/* Header */}
      <View style={[styles.header, dynamicStyles.header]}>
        <Text style={[styles.headerTitle, dynamicStyles.headerTitle]}>{clubData?.clubName || 'Club Dashboard'}</Text>
        {clubData && (
          <View style={styles.headerBadges}>
            {!clubData.claimedBy && (
              <View style={[styles.unclaimedBadge, dynamicStyles.unclaimedBadge]}>
                <Text style={styles.unclaimedBadgeText}>Unclaimed</Text>
              </View>
            )}
            {clubData.tier && (
              <View style={[styles.tierBadge, dynamicStyles.tierBadge, clubData.tier === 'PRO' && dynamicStyles.tierBadgePro]}>
                <Text style={[styles.tierBadgeText, dynamicStyles.tierBadgeText]}>
                  {clubData.tier === 'PRO' ? '⭐ Pro' : 'Free'}
                </Text>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Tabs */}
      <View style={[styles.tabs, dynamicStyles.tabs]}>
        {isMobile ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabsScrollContentMobile}
            style={styles.tabsScrollView}
          >
            <TouchableOpacity
              style={[styles.tab, styles.tabMobile, activeTab === 'info' && styles.activeTab]}
              onPress={() => setActiveTab('info')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'info' && dynamicStyles.activeTabText]}>
                Club Info
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, styles.tabMobile, activeTab === 'events' && styles.activeTab]}
              onPress={() => setActiveTab('events')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'events' && dynamicStyles.activeTabText]}>
                Events
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, styles.tabMobile, activeTab === 'members' && styles.activeTab]}
              onPress={() => setActiveTab('members')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'members' && dynamicStyles.activeTabText]}>
                Members
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, styles.tabMobile, activeTab === 'tournaments' && styles.activeTab]}
              onPress={() => setActiveTab('tournaments')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'tournaments' && dynamicStyles.activeTabText]}>
                Tournaments
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, styles.tabMobile, activeTab === 'locations' && styles.activeTab]}
              onPress={() => setActiveTab('locations')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'locations' && dynamicStyles.activeTabText]}>
                Locations
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, styles.tabMobile, activeTab === 'payments' && styles.activeTab]}
              onPress={() => setActiveTab('payments')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'payments' && dynamicStyles.activeTabText]}>
                Payments
              </Text>
            </TouchableOpacity>
          </ScrollView>
        ) : (
          <>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'info' && styles.activeTab]}
              onPress={() => setActiveTab('info')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'info' && dynamicStyles.activeTabText]}>
                Club Info
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'events' && styles.activeTab]}
              onPress={() => setActiveTab('events')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'events' && dynamicStyles.activeTabText]}>
                Events
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'members' && styles.activeTab]}
              onPress={() => setActiveTab('members')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'members' && dynamicStyles.activeTabText]}>
                Members
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'tournaments' && styles.activeTab]}
              onPress={() => setActiveTab('tournaments')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'tournaments' && dynamicStyles.activeTabText]}>
                Tournaments
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'locations' && styles.activeTab]}
              onPress={() => setActiveTab('locations')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'locations' && dynamicStyles.activeTabText]}>
                Locations
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'payments' && styles.activeTab]}
              onPress={() => setActiveTab('payments')}
            >
              <Text style={[styles.tabText, dynamicStyles.tabText, activeTab === 'payments' && dynamicStyles.activeTabText]}>
                Payments
              </Text>
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* Tab Content */}
      {activeTab === 'info' && renderClubInfo()}
      {activeTab === 'events' && renderEvents()}
      {activeTab === 'members' && renderMembers()}
      {activeTab === 'tournaments' && renderTournaments()}
      {activeTab === 'locations' && renderLocations()}
      {activeTab === 'payments' && renderPayments()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
  },
  tabsScrollContent: {
    flexDirection: 'row',
    paddingHorizontal: 4,
  },
  tabsScrollContentMobile: {
    width: 720,
    flexDirection: 'row',
    paddingHorizontal: 4,
  },
  tabsScrollView: {
    flex: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabMobile: {
    flex: 1,
    minWidth: 0,
  },
  activeTab: {
    borderBottomColor: '#94EB98',
  },
  tabText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: '#333',
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
  analyticsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  analyticsButtonText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginLeft: 10,
  },
  infoSection: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  infoTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginLeft: 8,
  },
  infoText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    marginLeft: 32,
  },
  link: {
    color: '#94EB98',
    textDecorationLine: 'underline',
  },
  recentPostsRow: {
    flexDirection: 'row',
    paddingLeft: 32,
    paddingVertical: 4,
  },
  recentPostThumb: {
    width: 220,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    marginRight: 12,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  recentPostImage: {
    width: '100%',
    height: 275,
    resizeMode: 'cover',
  },
  recentPostPlaceholder: {
    width: 220,
    height: 275,
    justifyContent: 'center',
    alignItems: 'center',
  },
  recentPostCaption: {
    fontSize: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    lineHeight: 18,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 60,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  emptyStateText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#999',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#bbb',
    textAlign: 'center',
  },
  memberHeaderSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
  },
  memberCountContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  memberCountText: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
  },
  visibilityToggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  visibilityLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  visibilityHelpText: {
    fontSize: 12,
    color: '#999',
    marginBottom: 16,
    paddingHorizontal: 4,
  },
  membersList: {
    gap: 12,
  },
  memberCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#eee',
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  memberAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  memberAvatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberDetails: {
    marginLeft: 12,
    flex: 1,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  memberJoinDate: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  memberLocation: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    gap: 2,
  },
  memberLocationText: {
    fontSize: 12,
    color: '#999',
  },
  removeMemberButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#FFEBEE',
  },
  eventCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    backgroundColor: '#f8f8f8',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  tournamentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: Platform.OS === 'ios' ? 0.05 : 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  eventCardContent: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  eventDateTime: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  eventMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  eventTypeTag: {
    backgroundColor: '#94EB98',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  eventTypeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
  },
  rsvpCount: {
    fontSize: 12,
    color: '#666',
  },
  createButtonRow: {
    flexDirection: 'row',
    gap: 8,
    margin: 16,
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: '#f0f0f0',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#ddd',
    borderStyle: 'dashed',
  },
  createButtonFlex: {
    flex: 1,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  createButtonDisabled: {
    opacity: 0.5,
  },
  createButtonTextDisabled: {
    color: '#999',
  },
  duplicateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 16,
    paddingHorizontal: 14,
    backgroundColor: '#E8F5E9',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#94EB98',
  },
  duplicateButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  recurringSection: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    padding: 16,
    backgroundColor: '#FAFAFA',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  recurringSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  recurringSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
  },
  addRecurringButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#94EB98',
    borderRadius: 8,
  },
  addRecurringText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  recurringEmptyText: {
    fontSize: 13,
    color: '#999',
    textAlign: 'center',
    paddingVertical: 12,
    lineHeight: 18,
  },
  headerBadges: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  unclaimedBadge: {
    backgroundColor: '#FFE0B2',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  unclaimedBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FF9800',
  },
  tierBadge: {
    backgroundColor: '#E0E0E0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  tierBadgePro: {
    backgroundColor: '#E8F5E9',
  },
  tierBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3E0',
    padding: 16,
    margin: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#FF9800',
    gap: 12,
  },
  warningText: {
    flex: 1,
    fontSize: 14,
    color: '#333',
  },
  warningButton: {
    backgroundColor: '#FF9800',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  warningButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  upgradeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    padding: 16,
    margin: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#94EB98',
    gap: 12,
  },
  upgradeBannerContent: {
    flex: 1,
  },
  upgradeBannerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  upgradeBannerText: {
    fontSize: 14,
    color: '#666',
  },
  upgradeBannerButton: {
    backgroundColor: '#94EB98',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  upgradeBannerButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  // Approval Block Styles
  backButton: {
    padding: 8,
  },
  approvalBlockContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  approvalBlockCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    maxWidth: 400,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderLeftWidth: 4,
  },
  approvalBlockPending: {
    borderColor: '#FFE0B2',
    borderLeftColor: '#FF9800',
  },
  approvalBlockRejected: {
    borderColor: '#FFCDD2',
    borderLeftColor: '#F44336',
  },
  approvalBlockTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 16,
    marginBottom: 12,
    textAlign: 'center',
  },
  approvalBlockText: {
    fontSize: 15,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 16,
  },
  rejectionReasonContainer: {
    backgroundColor: '#FFEBEE',
    borderRadius: 8,
    padding: 12,
    width: '100%',
    marginBottom: 16,
  },
  rejectionReasonLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#C62828',
    marginBottom: 4,
  },
  rejectionReasonText: {
    fontSize: 14,
    color: '#C62828',
    lineHeight: 20,
  },
  submittedAtText: {
    fontSize: 13,
    color: '#999',
    marginBottom: 20,
  },
  goBackButton: {
    backgroundColor: '#94EB98',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 10,
  },
  goBackButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
});

export default ClubDashboardScreen;

